import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

type Status = 'done' | 'in_progress' | 'needs_refinement' | 'planned' | 'exploring';

interface Item {
  id: string;
  title: string;
  description: string | null;
  category: string;
  status: Status;
  sort_order: number;
  updated_at: string;
}

const SECTIONS: { status: Status; label: string; cls: string }[] = [
  { status: 'done', label: 'Done', cls: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' },
  { status: 'in_progress', label: 'In Progress', cls: 'bg-amber-500/10 text-amber-300 border-amber-500/30' },
  { status: 'needs_refinement', label: 'Refining', cls: 'bg-orange-500/10 text-orange-300 border-orange-500/30' },
  { status: 'planned', label: 'Planned', cls: 'bg-[#C9A84C]/10 text-[#C9A84C] border-[#C9A84C]/30' },
  { status: 'exploring', label: 'Exploring', cls: 'bg-[#FAF8F5]/5 text-[#FAF8F5]/60 border-[#2A2A35]/50' },
];

export function RoadmapPreview() {
  const [top, setTop] = useState<Partial<Record<Status, Item>>>({});
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data, error } = await (supabase as any)
          .from('roadmap_items')
          .select('id,title,description,category,status,sort_order,updated_at')
          .eq('is_public', true)
          .order('sort_order', { ascending: true })
          .order('updated_at', { ascending: false });
        if (error) throw error;
        if (!alive) return;
        const map: Partial<Record<Status, Item>> = {};
        for (const row of (data ?? []) as Item[]) {
          if (!map[row.status]) map[row.status] = row;
        }
        setTop(map);
      } catch {
        if (alive) setFailed(true);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (failed) return null;
  const hasAny = SECTIONS.some((s) => top[s.status]);
  if (loaded && !hasAny) return null;

  return (
    <section className="py-24 px-6 bg-[#0D0D12]">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-[#C9A84C]/70 font-mono text-xs uppercase tracking-widest mb-3">
              The Roadmap
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-[#FAF8F5] tracking-tight">
              What we are building.
            </h2>
            <p className="text-[#FAF8F5]/40 mt-3 max-w-xl">
              A live look at what is shipped, what we are polishing, and what is next.
            </p>
          </div>
          <Link
            to="/roadmap"
            className="hidden sm:inline-flex items-center gap-2 text-sm text-[#FAF8F5]/60 hover:text-[#C9A84C] transition-colors whitespace-nowrap"
          >
            View full roadmap <ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {SECTIONS.map((s) => {
            const item = top[s.status];
            if (!loaded) {
              return (
                <div
                  key={s.status}
                  className="h-44 rounded-2xl bg-[#1a1a24]/40 border border-[#2A2A35]/30 animate-pulse"
                />
              );
            }
            if (!item) return null;
            return (
              <Link
                key={s.status}
                to="/roadmap"
                className="group flex flex-col gap-3 p-5 rounded-2xl bg-[#1a1a24]/40 border border-[#2A2A35]/40 hover:border-[#C9A84C]/40 hover:bg-[#1a1a24]/70 transition-all"
              >
                <span
                  className={`self-start text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.cls}`}
                >
                  {s.label}
                </span>
                <h3 className="text-base font-semibold text-[#FAF8F5] leading-snug line-clamp-2">
                  {item.title}
                </h3>
                {item.description && (
                  <p className="text-xs text-[#FAF8F5]/45 leading-relaxed line-clamp-3">
                    {item.description}
                  </p>
                )}
                <span className="mt-auto text-[10px] uppercase tracking-wider text-[#FAF8F5]/30">
                  {item.category}
                </span>
              </Link>
            );
          })}
        </div>

        <div className="mt-8 sm:hidden text-center">
          <Link
            to="/roadmap"
            className="inline-flex items-center gap-2 text-sm text-[#FAF8F5]/60 hover:text-[#C9A84C] transition-colors"
          >
            View full roadmap <ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </Link>
        </div>
      </div>
    </section>
  );
}
